import { Form, Badge } from '@frontend/design_system'
import {
  ProfileFormValues,
  ProfileDetailsFormProps,
} from '../../../../../ui/components/ProfileDetailsForm/types'
import { initialFormValues } from 'ui/components/ProfileDetailsForm/constants'
import styles from './styles.module.scss'
import { ProfileDetailsFormWrapperProps } from './types'
import {
  PROFILE_ADD_CONFIRM_MODAL,
  PROFILE_DETAILS,
} from 'texts/profileDetails'
import { ProfileDetailsForm } from 'ui/components/ProfileDetailsForm'
import { FormChildren } from 'types/form'
import { Confirmation } from 'ui/molecules/Confirmation'
import { useEffect, useMemo, useRef, useState } from 'react'
import { COMPLETE_CONFIRMATION } from 'texts/common'
import {
  isMigratedUserFlowSelector,
  isOperatorSelector,
  userDataSelector,
} from 'redux/login/selectors'
import { useAppSelector } from 'redux/hooks'
import { CONFIRM } from 'texts/uiTexts'
import { useGetProfileReferences } from 'hooks/useGetProfileReferences'
import { REGISTRATION_STATUSES } from 'api/auth/constants'
import { ApprovalInfoBadges } from 'ui/components/ApprovalInfoBadges'
import { PROFILE_DETAILS_FORM_ID } from 'ui/components/ProfileListContent/constants'
import { CustomFile } from 'ui/atoms/UploadFile/types'
import { LogoForm } from 'ui/components/ProfileDetailsForm/components/LogoForm/LogoForm'
import { UnsavedChangesModal } from 'ui/components/UnsavedChangesModal'

export const ProfileDetailsFormWrapper = ({
  handleSubmit,
  initialValues = initialFormValues,
  readOnly,
  isUserApproved,
  isProfileAddForm = false,
}: ProfileDetailsFormWrapperProps) => {
  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false)
  const [formValues, setFormValues] = useState<Partial<ProfileFormValues>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isOperator = useAppSelector(isOperatorSelector)
  const { sectionsStatuses, migrationSectionsStatuses } =
    useAppSelector(userDataSelector)

  const [logo, setLogo] = useState(initialValues.logo)
  const [logoError, setLogoError] = useState(false)
  const isMigratedUserFlow = useAppSelector(isMigratedUserFlowSelector)

  const logoRef = useRef<HTMLDivElement>(null)

  const checkLogo = () => {
    let isLogoError = false
    if (!logo?.length) {
      isLogoError = true
    }
    setLogoError(isLogoError)

    if (isLogoError) {
      logoRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }
    return isLogoError
  }

  const handleAddLogo = (logo: CustomFile[]) => {
    if (logo.length) {
      setLogo(logo)
      setLogoError(false)
    }
  }

  const isPendingUser =
    !isOperator &&
    sectionsStatuses?.profileStatus !== REGISTRATION_STATUSES.APPROVED

  const formKeyRef = useRef(0)
  const formKey = useMemo(() => (formKeyRef.current += 1), [initialValues])

  const { countries, currencies } = useGetProfileReferences(initialValues)

  const onSubmit = (data: Partial<ProfileFormValues>, validate: boolean) => {
    setIsSubmitting(false)
    const isLogoError = checkLogo()

    if (validate && !isLogoError) {
      setIsSubmitting(true)
      if (!isUserApproved || isProfileAddForm) {
        setConfirmModalOpen(true)
        setFormValues(data)
      } else if (handleSubmit) {
        handleSubmit({
          ...data,
          phoneAreaCode: data.phoneCor?.code
            ? String(data.phoneCor?.phoneCode)
            : '',
          phoneCountryCode: data.phoneCor?.code
            ? String(data.phoneCor?.code)
            : '',
          companyCountryID: data.companyStateID || data.companyCountryID,
          state: data.companyStateID ? '' : data.state,
          logo: logo,
        })
      }
    }
  }

  const handleConfirm = async () => {
    try {
      setIsSubmitting(true)
      if (handleSubmit && formValues) {
        handleSubmit({
          ...formValues,
          phoneAreaCode: formValues.phoneCor?.code
            ? String(formValues.phoneCor?.phoneCode)
            : '',
          phoneCountryCode: formValues.phoneCor?.code
            ? String(formValues.phoneCor?.code)
            : '',
          companyCountryID:
            formValues.companyStateID || formValues.companyCountryID,
          state: formValues.companyStateID ? '' : formValues.state,
          logo: logo,
        })
      }
    } finally {
      setConfirmModalOpen(false)
      setIsSubmitting(false)
    }
  }

  useEffect(() => {
    if (isMigratedUserFlow) {
      checkLogo()
    }
  }, [isMigratedUserFlow])

  return (
    <>
      <Form
        initValues={initialValues}
        onSubmit={onSubmit}
        className={styles['form-wrapper']}
        formId={PROFILE_DETAILS_FORM_ID}
        reduxId={PROFILE_DETAILS_FORM_ID}
        key={formKey}
      >
        {({ errors, values, isDirty }: FormChildren) => (
          <>
            {isConfirmModalOpen && (
              <Confirmation
                confirmModalProps={{
                  question: isProfileAddForm
                    ? `${PROFILE_ADD_CONFIRM_MODAL.QUESTION} ${formValues.companyName}?`
                    : COMPLETE_CONFIRMATION.CONFIRMATION_MODAL.QUESTION,
                  onClose: () => setConfirmModalOpen(false),
                  onConfirm: handleConfirm,
                  title: isProfileAddForm
                    ? PROFILE_ADD_CONFIRM_MODAL.TITLE
                    : COMPLETE_CONFIRMATION.CONFIRMATION_MODAL.TITLE,
                  confirmLabel: isProfileAddForm
                    ? CONFIRM
                    : COMPLETE_CONFIRMATION.CONFIRMATION_MODAL.BUTTON_LABEL,
                }}
              />
            )}
            <UnsavedChangesModal isDirty={isDirty && !isSubmitting} />
            {isPendingUser && (
              <ApprovalInfoBadges
                isRequested={
                  sectionsStatuses?.profileStatus ===
                  REGISTRATION_STATUSES.REQUESTED
                }
              />
            )}
            {isMigratedUserFlow &&
              migrationSectionsStatuses?.profileStatus ===
                REGISTRATION_STATUSES.INCOMPLETE && (
                <Badge
                  color="yellow800"
                  typographyName="body1WMedium"
                  type="warning"
                  message={PROFILE_DETAILS.ONBOARDING_MESSAGE}
                  className={styles['onboarding-badge']}
                />
              )}
            <LogoForm
              logoError={logoError}
              handleAddLogo={handleAddLogo}
              readOnly={!!readOnly}
              logo={logo}
              ref={logoRef}
              rating={Number(values.rating)}
              reviewCount={Number(values.reviewCount)}
            />
            <ProfileDetailsForm
              errors={errors}
              values={values as ProfileDetailsFormProps['values']}
              readOnly={readOnly}
              currencies={currencies}
              countries={countries}
            />
          </>
        )}
      </Form>
    </>
  )
}
